/**
 * Consent Templates Manager — admin page.
 * Route: /dashboard/dental/consents/templates
 *
 * Lists tenant templates (defaults + custom), allows create/edit/soft-delete.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../App";
import AdminLayout from "../../components/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import {
  FileSignature, Plus, Loader2, Edit2, Trash2, ShieldCheck, ScrollText,
  Stethoscope, ClipboardCheck, FileText,
} from "lucide-react";
import { useIsDentalTenant } from "../../hooks/useIsDentalTenant";
import { Navigate } from "react-router-dom";

const CATEGORIES = [
  { key: "privacy",        label: "Privacy GDPR",          icon: ShieldCheck },
  { key: "data_processing",label: "Trattamento dati",      icon: ScrollText },
  { key: "healthcare",     label: "Consenso sanitario",    icon: Stethoscope },
  { key: "treatment_plan", label: "Piano di cure",         icon: ClipboardCheck },
  { key: "custom",         label: "Personalizzato",        icon: FileText },
];

const catMeta = (k) => CATEGORIES.find(c => c.key === k) || CATEGORIES[CATEGORIES.length - 1];

const ConsentTemplatesPage = () => {
  const isDental = useIsDentalTenant();
  const [list, setList]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // template object | "new" | null
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/dental/consents/templates`, { withCredentials: true });
      setList(r.data.templates || []);
    } catch {
      toast.error("Errore caricamento template");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!isDental) return <Navigate to="/dashboard" replace />;

  const save = async (payload) => {
    try {
      if (editing && editing !== "new" && editing.template_id) {
        await axios.patch(`${API}/dental/consents/templates/${editing.template_id}`, payload, { withCredentials: true });
        toast.success("Template aggiornato");
      } else {
        await axios.post(`${API}/dental/consents/templates`, payload, { withCredentials: true });
        toast.success("Template creato");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore salvataggio");
    }
  };

  const remove = async (tpl) => {
    try {
      await axios.delete(`${API}/dental/consents/templates/${tpl.template_id}`, { withCredentials: true });
      toast.success("Template eliminato");
      setConfirmDel(null);
      load();
    } catch {
      toast.error("Errore eliminazione");
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6" data-testid="consent-templates-page">
        {/* HERO */}
        <div className="rounded-3xl border border-slate-200/60 dark:border-white/10 bg-gradient-to-br from-violet-500/5 via-transparent to-cyan-500/5 dark:from-violet-500/10 dark:to-cyan-500/10 p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center shadow-lg">
                <FileSignature className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight">
                  Template consensi
                </h1>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-lg">
                  Gestisci i moduli che potrai inviare ai pazienti per la firma digitale. I 4 template predefiniti
                  (Privacy GDPR, Trattamento dati, Consenso sanitario, Piano di cure) sono già pronti all'uso.
                </p>
              </div>
            </div>
            <Button
              onClick={() => setEditing("new")}
              className="rounded-full bg-gradient-to-r from-violet-600 to-cyan-500 hover:opacity-90 text-white"
              data-testid="consent-template-new-btn"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Nuovo template
            </Button>
          </div>
        </div>

        {/* LIST */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[0,1,2,3].map(i => (
              <div key={i} className="h-32 rounded-2xl bg-slate-100 dark:bg-white/5 animate-pulse" />
            ))}
          </div>
        ) : list.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-slate-200 dark:border-white/10 p-10 text-center">
            <p className="text-slate-500">Nessun template ancora.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="consent-templates-list">
            {list.map(t => {
              const meta = catMeta(t.category);
              const Icon = meta.icon;
              return (
                <div
                  key={t.template_id}
                  className="group rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white dark:bg-slate-900/60 p-4 hover:border-violet-300 hover:shadow-md transition-all"
                  data-testid={`consent-template-card-${t.template_id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-100 to-slate-50 dark:from-white/10 dark:to-white/5 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-4 h-4 text-violet-500" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-slate-900 dark:text-white truncate">{t.name}</h3>
                          {t.is_default && (
                            <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300">
                              Default
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mt-0.5">{meta.label}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        size="sm" variant="ghost"
                        className="h-7 w-7 rounded-full p-0"
                        onClick={() => setEditing(t)}
                        data-testid={`consent-template-edit-${t.template_id}`}
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </Button>
                      {!t.is_default && (
                        <Button
                          size="sm" variant="ghost"
                          className="h-7 w-7 rounded-full p-0 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                          onClick={() => setConfirmDel(t)}
                          data-testid={`consent-template-delete-${t.template_id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-3 line-clamp-3 leading-relaxed">{t.body}</p>
                </div>
              );
            })}
          </div>
        )}

        {/* EDITOR */}
        <TemplateEditor
          open={editing !== null}
          template={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSave={save}
        />

        {/* DELETE CONFIRM */}
        <Dialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Eliminare il template?</DialogTitle>
              <DialogDescription>
                Il template "<strong>{confirmDel?.name}</strong>" verrà disattivato. Le firme già archiviate restano valide.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmDel(null)}>Annulla</Button>
              <Button
                onClick={() => remove(confirmDel)}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="consent-template-delete-confirm"
              >
                Elimina
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

/* ============================ EDITOR DIALOG ============================ */
const TemplateEditor = ({ open, template, onClose, onSave }) => {
  const [name, setName]   = useState("");
  const [body, setBody]   = useState("");
  const [cat, setCat]     = useState("custom");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(template?.name || "");
      setBody(template?.body || "");
      setCat(template?.category || "custom");
    }
  }, [open, template]);

  const submit = async () => {
    if (!name.trim() || !body.trim()) {
      toast.error("Nome e contenuto sono obbligatori");
      return;
    }
    setSaving(true);
    await onSave({ name: name.trim(), body: body.trim(), category: cat });
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="consent-template-editor">
        <DialogHeader>
          <DialogTitle>{template ? "Modifica template" : "Nuovo template"}</DialogTitle>
          <DialogDescription>
            Il testo sarà mostrato al paziente nel wizard di firma. Usa un linguaggio chiaro e conforme alla normativa.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Nome</label>
            <Input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Es: Consenso radiografie panoramiche"
              data-testid="consent-template-name"
            />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Categoria</label>
            <Select value={cat} onValueChange={setCat}>
              <SelectTrigger data-testid="consent-template-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map(c => (
                  <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Testo del modulo</label>
            <Textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              rows={10}
              className="font-mono text-xs leading-relaxed"
              placeholder="Scrivi qui il testo che il paziente leggerà…"
              data-testid="consent-template-body"
            />
            <p className="text-[10px] text-slate-400 mt-1">{body.length} caratteri</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Annulla</Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-gradient-to-r from-violet-600 to-cyan-500 hover:opacity-90 text-white"
            data-testid="consent-template-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConsentTemplatesPage;
